// Recolors the default WerkPilot PNG logo to a target accent color.
// Strategy: load the source image into a canvas, convert each colored
// pixel to HSL, replace hue+saturation with the target color while
// keeping the original lightness. Pixels that are essentially gray/white
// (low saturation) are left untouched so the white tools stay white.

const DEFAULT_LOGO_SRC = '/icons/icon-512x512.png';
const DEFAULT_ACCENT = '#378ab2';

// Cache: targetHex -> data URL Promise
const cache = new Map();
let sourcePixelsPromise = null;

// --- Color conversion helpers ----------------------------------------------

const hexToRgb = (hex) => {
  const clean = hex.replace('#', '');
  const num = parseInt(clean, 16);
  return {
    r: (num >> 16) & 0xff,
    g: (num >> 8) & 0xff,
    b: num & 0xff,
  };
};

const rgbToHsl = (r, g, b) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
    }
    h /= 6;
  }
  return { h, s, l };
};

const hue2rgb = (p, q, t) => {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
};

const hslToRgb = (h, s, l) => {
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  };
};

// --- Source loading --------------------------------------------------------

const loadSourcePixels = () => {
  if (sourcePixelsPromise) return sourcePixelsPromise;

  sourcePixelsPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        resolve({ imageData, width: canvas.width, height: canvas.height });
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (err) => reject(err);
    img.src = DEFAULT_LOGO_SRC;
  });

  // Reset cache on failure so a later call can retry
  sourcePixelsPromise.catch(() => {
    sourcePixelsPromise = null;
  });

  return sourcePixelsPromise;
};

// --- Public API ------------------------------------------------------------

export const isDefaultAccent = (hex) => {
  if (!hex) return true;
  return hex.trim().toLowerCase() === DEFAULT_ACCENT;
};

/**
 * Returns a Promise resolving to a data URL for the WerkPilot default logo,
 * tinted to the given hex color. Falls back to the original PNG path on error.
 */
export const getTintedDefaultLogo = (targetHex) => {
  if (!targetHex || isDefaultAccent(targetHex)) {
    return Promise.resolve(DEFAULT_LOGO_SRC);
  }

  const key = targetHex.trim().toLowerCase();
  if (cache.has(key)) return cache.get(key);

  const promise = loadSourcePixels()
    .then(({ imageData, width, height }) => {
      const targetRgb = hexToRgb(key);
      const targetHsl = rgbToHsl(targetRgb.r, targetRgb.g, targetRgb.b);

      // Work on a copy so the cached source pixels stay clean.
      const out = new ImageData(
        new Uint8ClampedArray(imageData.data),
        width,
        height
      );
      const data = out.data;

      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a === 0) continue;

        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const hsl = rgbToHsl(r, g, b);

        // Leave near-grayscale pixels (white tools, neutral edges) untouched.
        if (hsl.s < 0.12) continue;

        const newRgb = hslToRgb(targetHsl.h, targetHsl.s, hsl.l);
        data[i] = newRgb.r;
        data[i + 1] = newRgb.g;
        data[i + 2] = newRgb.b;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').putImageData(out, 0, 0);
      return canvas.toDataURL('image/png');
    })
    .catch((err) => {
      console.warn('Logo-Tinting fehlgeschlagen, Standard-Logo wird verwendet:', err);
      cache.delete(key);
      return DEFAULT_LOGO_SRC;
    });

  cache.set(key, promise);
  return promise;
};

export const DEFAULT_LOGO_PATH = DEFAULT_LOGO_SRC;
export const DEFAULT_ACCENT_COLOR = DEFAULT_ACCENT;
